import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { EnvironmentService } from './environment/environment.service';
import { UserDataService } from './user-data-service';
import { TabIdentityService } from './tab-identity.service';

export type RefineryAccessMode = 'edit' | 'readOnly' | 'none';

/**
 * Owns the refinery Edit lock — deliberately a root-provided singleton, NOT
 * state living on NodedefinitionComponent (see the notes on route-based
 * start/stop below, unchanged from the original presence service).
 *
 * ACCESS MODEL: opening a refinery is ALWAYS Read-Only by default, for every
 * tab, whether the refinery is shared or not — no lock is requested and no
 * heartbeat starts just from viewing. Exactly one (UserId, TabId) may hold
 * the Edit lock for a given refinery at a time; requesting it is an
 * explicit action (requestEdit). Any number of Read-Only viewers can be
 * open at once, including other tabs of the same user who holds the lock,
 * and including the lock holder's own other tabs — the lock is keyed on
 * (UserId, TabId) together, not UserId alone, specifically so two tabs of
 * the SAME logged-in user are never confused with each other. See
 * TabIdentityService for why a tab id exists at all: the dtr_session cookie
 * is shared by every tab of the same browser profile and can't express
 * this.
 *
 * The server is the sole authority — see ConfigController's
 * refineryPresence/* endpoints and CheckEditLockAsync, which every mutating
 * refinery/node endpoint now enforces independently of anything this
 * service or the UI decides. This service exists so the UI doesn't have to
 * find out about a rejected mutation to know it's Read-Only; it is not
 * itself the security boundary.
 *
 * Talks to the presence endpoints directly (HttpClient + EnvironmentService)
 * rather than through ConfigService: AuthService needs to release the Edit
 * lock on logout, and ConfigService already depends on AuthService for user
 * details — routing presence calls through ConfigService would create
 * AuthService -> RefineryPresenceService -> ConfigService -> AuthService, a
 * circular DI dependency Angular refuses to construct (NG0200).
 */
@Injectable({ providedIn: 'root' })
export class RefineryPresenceService {
  private static readonly HEARTBEAT_INTERVAL_MS = 20000;

  private heartbeatTimer: any = null;
  private currentRefineryGuid: string | null = null;

  private readonly _mode$ = new BehaviorSubject<RefineryAccessMode>('none');
  readonly mode$ = this._mode$.asObservable();

  /** Fires when a held Edit lock is found to be lost — expired and taken by
   * someone else between heartbeats (network drop, suspended laptop, a
   * missed beat) — so the UI can tell the Analyst why they were just
   * dropped to Read-Only, instead of it happening silently. */
  private readonly _lockLost$ = new Subject<{ holderUserName: string | null }>();
  readonly lockLost$ = this._lockLost$.asObservable();

  constructor(
    private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private userData: UserDataService,
    private router: Router,
    private tabIdentity: TabIdentityService
  ) {
    // Registered once for the lifetime of the app (this service is a
    // singleton) — not per-component, so it can't accumulate or go missing
    // depending on which component happened to be alive when the tab closed.
    window.addEventListener('beforeunload', () => this.releaseImmediatelyViaBeacon());

    // Release the moment the Analyst actually navigates away from the
    // refinery workflow — not just on explicit actions like picking a
    // different refinery from the list. Without this, the Edit lock stayed
    // held for as long as the tab was open even after moving on to an
    // unrelated page, since no component destroy ever corresponded to
    // "actually left".
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      const url = (e as NavigationEnd).urlAfterRedirects;

      // /refresh is not a real destination — it's a momentary, deliberate
      // bounce route (base-transformer-ui.component.ts, the ONLY place in
      // the codebase that navigates there) used purely to force Angular to
      // re-instantiate a transform-detail component when the Analyst
      // re-selects the SAME transformer. Evaluating it as "left the
      // workflow" released the lock for that split second even though the
      // Analyst never actually navigated anywhere — skip it entirely.
      if (RefineryPresenceService.isTransientBounceUrl(url)) {
        return;
      }

      if (!RefineryPresenceService.isRefineryWorkflowUrl(url)) {
        this.stop();
      }
    });
  }

  private static isTransientBounceUrl(url: string): boolean {
    if (!url) return false;
    return url.split('?')[0].split('#')[0] === '/refresh';
  }

  /** True for the refinery canvas itself (/node) and every child route
   * under dataTransformConfig/ (ingest, transform/finalize, every
   * *TransformDetail page, configDetail) — confirmed directly against the
   * route definitions in app.module.ts, not guessed from page names.
   * Matching by URL prefix means any transform-detail route added later is
   * automatically covered too, with nothing here to update. */
  private static isRefineryWorkflowUrl(url: string): boolean {
    if (!url) return false;
    const path = url.split('?')[0].split('#')[0];
    return path === '/node' || path.includes('/dataTransformConfig/');
  }

  get mode(): RefineryAccessMode {
    return this._mode$.value;
  }

  get tabId(): string {
    return this.tabIdentity.tabId;
  }

  private acquireEditUrl(): string {
    return `${this.environmentService.activeAppBaseUrl}/api/Config/refineryPresence/acquireEdit`;
  }

  private heartbeatUrl(): string {
    return `${this.environmentService.activeAppBaseUrl}/api/Config/refineryPresence/heartbeat`;
  }

  private releaseUrl(): string {
    return `${this.environmentService.activeAppBaseUrl}/api/Config/refineryPresence/release`;
  }

  /** Batch — the refinery LIST needs "who's busy" for every row at once,
   * not N round trips for N refineries. */
  async getActivePresence(): Promise<{ [guid: string]: { userId: number, userName: string } }> {
    const requestUrl = `${this.environmentService.activeAppBaseUrl}/api/Config/refineryPresence/active`;
    const result = await this.httpClient.get<{ [guid: string]: { userId: number, userName: string } }>(requestUrl).toPromise();
    return result ?? {};
  }

  /** Live, single-refinery presence check — used right before actually
   * opening a refinery, not the batch list snapshot above. The list's busy
   * column is only a hint from whenever it last loaded; this is the check
   * that must be trusted at the moment of opening. */
  async checkPresence(refineryGuid: string): Promise<{ isBusy: boolean, userId: number | null, userName: string | null, tabId: string | null }> {
    const requestUrl = `${this.environmentService.activeAppBaseUrl}/api/Config/refineryPresence/check/${refineryGuid}`;
    const result = await this.httpClient.get<{ isBusy: boolean, userId: number | null, userName: string | null, tabId: string | null }>(requestUrl).toPromise();
    return result ?? { isBusy: false, userId: null, userName: null, tabId: null };
  }

  /**
   * True only when the refinery is busy AND held by someone OTHER than the
   * currently logged-in Analyst. A refinery the Analyst's own user already
   * holds the Edit lock on in ANOTHER tab is not a conflict for the list's
   * "busy" gate — they should still be able to open it Read-Only, or open a
   * further tab. Centralized here rather than duplicated at every call
   * site — deliberately compares userId, not userName, since that's the
   * identity the lock itself is keyed on.
   */
  isBusyByAnotherUser(presence: { isBusy: boolean, userId: number | null } | null | undefined): boolean {
    if (!presence?.isBusy) return false;
    return String(presence.userId) !== String(this.userData.getUserId());
  }

  /** Marks this tab as viewing a refinery Read-Only — the default for every
   * open. Requests no lock and starts no heartbeat; always safe to call for
   * any refinery, shared or private, busy or not. */
  viewReadOnly(refineryGuid: string): void {
    this.stopHeartbeatOnly();
    this.currentRefineryGuid = refineryGuid ?? null;
    this._mode$.next(refineryGuid ? 'readOnly' : 'none');
  }

  /**
   * Explicitly requests the Edit lock for a refinery. Grants it only if no
   * other (UserId, TabId) currently holds it — a different tab of the SAME
   * user counts as "other" here too. Re-requesting from the tab that
   * already holds it is idempotent. On grant, starts the renewing
   * heartbeat; on denial, mode stays/returns to Read-Only.
   */
  async requestEdit(refineryGuid: string): Promise<{ granted: boolean, holderUserId: number | null, holderUserName: string | null }> {
    const body = { refineryGuid, tabId: this.tabId };
    const result = await this.httpClient
      .post<{ granted: boolean, holderUserId: number | null, holderUserName: string | null, holderTabId: string | null }>(this.acquireEditUrl(), body)
      .toPromise();

    this.currentRefineryGuid = refineryGuid;
    if (result?.granted) {
      this._mode$.next('edit');
      this.startHeartbeat();
    } else {
      this._mode$.next('readOnly');
    }
    return result ?? { granted: false, holderUserId: null, holderUserName: null };
  }

  // Matches the backend's PresenceStaleAfter (75s, ConfigController.cs).
  // After this many CONSECUTIVE failed heartbeats (~80s), the server-side
  // lock is certainly stale regardless of what this tab still believes —
  // waiting any longer would just mean this tab keeps claiming Edit mode
  // the backend has already stopped honoring, silently drifting out of
  // sync until a save/run/reset gets rejected with no explanation.
  private static readonly MAX_CONSECUTIVE_HEARTBEAT_FAILURES = 4;

  private startHeartbeat(): void {
    this.stopHeartbeatOnly();
    let consecutiveFailures = 0;
    const sendHeartbeat = async () => {
      if (!this.currentRefineryGuid || this.mode !== 'edit') return;
      try {
        const result = await this.httpClient
          .post<{ renewed: boolean, holderUserName?: string | null }>(
            this.heartbeatUrl(),
            { refineryGuid: this.currentRefineryGuid, tabId: this.tabId }
          )
          .toPromise();
        consecutiveFailures = 0;
        if (result && result.renewed === false) {
          // The lock was lost — expired and taken by someone else between
          // heartbeats. Drop to Read-Only rather than let this tab keep
          // believing it's still safe to save; the backend would reject the
          // save anyway, but surfacing it here is far clearer to the Analyst.
          this.stopHeartbeatOnly();
          this._mode$.next('readOnly');
          this._lockLost$.next({ holderUserName: result.holderUserName ?? null });
        }
      } catch (err) {
        consecutiveFailures++;
        console.error(`Failed to send refinery edit-lock heartbeat (${consecutiveFailures} in a row):`, err);
        if (consecutiveFailures >= RefineryPresenceService.MAX_CONSECUTIVE_HEARTBEAT_FAILURES) {
          this.stopHeartbeatOnly();
          this._mode$.next('readOnly');
          this._lockLost$.next({ holderUserName: null });
        }
      }
    };
    sendHeartbeat(); // don't wait a full interval before the first beat
    this.heartbeatTimer = setInterval(sendHeartbeat, RefineryPresenceService.HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeatOnly(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * One-off Edit-lock acquire → action → release for a refinery this tab is
   * NOT actively "open" in — e.g. toggling Shared/Public from the refinery
   * LIST, where every row's save still goes through the same backend
   * Edit-lock check as any other save. Deliberately does NOT touch mode$/
   * currentRefineryGuid (this tab's own tracked open-refinery state) and
   * starts no heartbeat — it just needs the lock held for the instant of
   * one save, then releases it immediately so it doesn't block anyone else
   * from actually opening that refinery to edit it.
   *
   * If this tab already holds the PERSISTENT Edit lock for this exact
   * refinery (i.e. it's the one currently open, not just a different list
   * row), runs the action directly instead — a transient acquire/release
   * around an already-held, ongoing edit session would incorrectly drop it.
   */
  async withTransientEditLock<T>(refineryGuid: string, action: () => Promise<T>): Promise<{ granted: boolean, holderUserName: string | null, result?: T }> {
    if (this.currentRefineryGuid === refineryGuid && this.mode === 'edit') {
      return { granted: true, holderUserName: null, result: await action() };
    }

    const acquireResult = await this.httpClient
      .post<{ granted: boolean, holderUserId: number | null, holderUserName: string | null }>(
        this.acquireEditUrl(), { refineryGuid, tabId: this.tabId }
      )
      .toPromise();

    if (!acquireResult?.granted) {
      return { granted: false, holderUserName: acquireResult?.holderUserName ?? null };
    }

    try {
      const result = await action();
      return { granted: true, holderUserName: null, result };
    } finally {
      await this.sendReleaseRequest(refineryGuid)
        .catch(err => console.error('Failed to release transient refinery edit lock:', err));
    }
  }

  /** A Config-API call came back 403 (lock enforcement) — the UI's idea of
   * its own mode was stale. Drop to Read-Only immediately without waiting
   * for the next heartbeat. Called from ConfigApiInterceptor. */
  forceReadOnly(): void {
    this.stopHeartbeatOnly();
    this._mode$.next(this.currentRefineryGuid ? 'readOnly' : 'none');
  }

  /** Leaves the refinery entirely — releases the Edit lock if held, clears
   * Read-Only tracking too. Call when navigating away from the refinery
   * workflow, picking a different refinery, or logging out. */
  stop(): void {
    const wasEditing = this.mode === 'edit';
    this.stopHeartbeatOnly();
    if (this.currentRefineryGuid && wasEditing) {
      this.sendReleaseRequest(this.currentRefineryGuid)
        .catch(err => console.error('Failed to release refinery edit lock:', err));
    }
    this.currentRefineryGuid = null;
    this._mode$.next('none');
  }

  private async sendReleaseRequest(refineryGuid: string): Promise<void> {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const body = JSON.stringify({ refineryGuid, tabId: this.tabId, userId: this.userData.getUserId() });
    await this.httpClient.post(this.releaseUrl(), body, { headers }).toPromise();
  }

  /** Browser-close/tab-close safety net: fetch()/HttpClient calls get
   * cancelled on unload, but navigator.sendBeacon is specifically designed
   * to reliably fire-and-forget during page teardown. Only the FAST path
   * for the clean-close case — the heartbeat's own server-side staleness
   * expiry is what guarantees the lock never gets stuck held forever even
   * if this never fires (crash, force-close, etc). */
  private releaseImmediatelyViaBeacon(): void {
    if (!this.currentRefineryGuid || this.mode !== 'edit') return;
    const payload = JSON.stringify({ refineryGuid: this.currentRefineryGuid, tabId: this.tabId, userId: this.userData.getUserId() });
    // 'text/plain', not 'application/json': a JSON content type is not
    // CORS-"simple", so the browser would need to run a CORS preflight
    // (OPTIONS) before the actual beacon request — a round trip that often
    // doesn't get to finish during page teardown, silently dropping the
    // release. text/plain avoids the preflight entirely; the server reads
    // the raw body itself rather than depending on the content type.
    navigator.sendBeacon(this.releaseUrl(), new Blob([payload], { type: 'text/plain' }));
  }
}
